import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  AtSign,
  Building2,
  MailSearch,
  MessageCircleMore,
  Network,
  PencilLine,
  Plus,
  Search,
  UsersRound,
} from 'lucide-react';
import {FormEvent, useMemo, useState} from 'react';
import {useSearchParams} from '../router';
import {api} from '../api';
import {Modal, PrimaryButton, SecondaryButton, Surface, Textarea, TextInput} from '../design';
import {Relationship, RelationshipStage, humanizeStage} from '../types';
import {EmptyPanel, ErrorPanel, LoadingPanel, PageHeader, formatRelativeDate} from '../components/PagePrimitives';
import {RelationshipDetail} from '../components/RelationshipDetail';

const stages: RelationshipStage[] = [
  'new',
  'active_conversation',
  'waiting',
  'follow_up',
  'dormant',
  'archived',
];
type RelationshipEdit = {
  id: string; name: string; emails: string; phone: string; agency: string; companyNames: string; nextAction: string;
};
const splitValues = (value: string): string[] => [...new Set(value.split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean))];

export const RelationshipsPage = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRelationshipId, setSelectedRelationshipId] = useState<string | null>(() => searchParams.get('focus'));
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [relationshipEdit, setRelationshipEdit] = useState<RelationshipEdit | null>(null);

  const relationships = useQuery({queryKey: ['relationships'], queryFn: api.relationships});

  const move = useMutation({
    mutationFn: ({id, stage}: {id: string; stage: RelationshipStage}) => api.moveRelationship(id, stage),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['relationships']}),
  });

  const create = useMutation({
    mutationFn: () =>
      api.createRelationship({
        name,
        emails: email ? [email] : [],
        stage: 'new',
      }),
    onSuccess: async () => {
      setName('');
      setEmail('');
      setCreateOpen(false);
      await queryClient.invalidateQueries({queryKey: ['relationships']});
      await queryClient.invalidateQueries({queryKey: ['action-items']});
    },
  });
  const saveRelationship = useMutation({
    mutationFn: (draft: RelationshipEdit) => api.updateRelationship(draft.id, {
      name: draft.name.trim(),
      emails: splitValues(draft.emails),
      phone: draft.phone.trim(),
      agency: draft.agency.trim(),
      companyNames: splitValues(draft.companyNames),
      nextAction: draft.nextAction.trim(),
    }),
    onSuccess: async () => {
      setRelationshipEdit(null);
      await queryClient.invalidateQueries({queryKey: ['relationships']});
    },
  });
  const editRelationship = (relationship: Relationship) => setRelationshipEdit({
    id: relationship.id,
    name: relationship.name,
    emails: relationship.emails.join('\n'),
    phone: relationship.phone ?? '',
    agency: relationship.agency ?? '',
    companyNames: (relationship.companyNames ?? []).join('\n'),
    nextAction: relationship.nextAction ?? '',
  });

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const items = relationships.data?.items ?? [];
    return needle
      ? items.filter((item) =>
          [item.name, item.agency, ...item.emails, ...(item.companyNames ?? [])].some((value) =>
            value?.toLowerCase().includes(needle),
          ),
        )
      : items;
  }, [query, relationships.data]);

  return (
    <>
      <PageHeader
        eyebrow="People before pipelines"
        title="Keep the relationship when the role changes."
        description="Recruiters and network contacts have their own durable history. One conversation can create several opportunities without losing the person behind them."
        actions={
          <PrimaryButton leftSection={<Plus size={17} aria-hidden="true" />} onClick={() => setCreateOpen(true)}>
            Add relationship
          </PrimaryButton>
        }
      />

      <div className="pd-board-toolbar">
        <label className="pd-search-control">
          <Search size={17} aria-hidden="true" />
          <span className="pd-visually-hidden">Filter relationships</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder="Filter by person, agency, company, or email"
          />
        </label>
        <span>{visible.length} visible</span>
      </div>

      {relationships.isLoading ? <LoadingPanel label="Loading relationships" /> : null}
      {relationships.isError ? (
        <ErrorPanel error={relationships.error} retry={() => void relationships.refetch()} />
      ) : null}
      {relationships.data && relationships.data.items.length === 0 ? (
        <EmptyPanel
          title="Your relationship board is waiting."
          description="Add a recruiter email, then connect Gmail to import matching threads. Adding PursuitDeck labels is a separate choice."
          action={
            <PrimaryButton onClick={() => setCreateOpen(true)} leftSection={<MailSearch size={16} />}>
              Add a recruiter
            </PrimaryButton>
          }
        />
      ) : null}

      {relationships.data && relationships.data.items.length > 0 ? (
        <div className="pd-relationship-board">
          {stages.map((stage) => {
            const entries = visible.filter((item) => item.stage === stage);
            return (
              <section key={stage} className="pd-relationship-stage" aria-labelledby={'relationship-' + stage}>
                <header>
                  <h3 id={'relationship-' + stage}>{humanizeStage(stage)}</h3>
                  <span>{entries.length}</span>
                </header>
                <div>
                  {entries.map((relationship) => (
                    <Surface key={relationship.id} className="pd-relationship-card">
                      <div className="pd-avatar" aria-hidden="true">
                        {relationship.name
                          .split(/\s+/)
                          .map((part) => part.charAt(0))
                          .join('')
                          .slice(0, 2)
                          .toUpperCase()}
                      </div>
                      <div className="pd-relationship-copy">
                        <h4>{relationship.name}</h4>
                        {relationship.agency ? (
                          <p>
                            <Building2 size={14} aria-hidden="true" />
                            {relationship.agency}
                          </p>
                        ) : null}
                        {relationship.emails[0] ? (
                          <p>
                            <AtSign size={14} aria-hidden="true" />
                            {relationship.emails[0]}
                          </p>
                        ) : null}
                        <div className="pd-relationship-stats">
                          <span>
                            <Network size={14} aria-hidden="true" />
                            {relationship.opportunityCount} opportunities
                          </span>
                          <span>
                            <MessageCircleMore size={14} aria-hidden="true" />
                            {formatRelativeDate(relationship.lastInteractionAt)}
                          </span>
                        </div>
                        {relationship.nextAction ? <strong>Next: {relationship.nextAction}</strong> : null}
                        <SecondaryButton
                          leftSection={<MessageCircleMore size={15} aria-hidden="true" />}
                          onClick={() => {
                            setSelectedRelationshipId(relationship.id);
                            const next = new URLSearchParams(searchParams);
                            next.set('focus', relationship.id);
                            setSearchParams(next, {replace: true});
                          }}
                        >
                          Open history
                        </SecondaryButton>
                        <SecondaryButton
                          leftSection={<PencilLine size={15} aria-hidden="true" />}
                          onClick={() => editRelationship(relationship)}
                        >
                          Edit relationship
                        </SecondaryButton>
                        <label className="pd-stage-control">
                          <span>Stage</span>
                          <select
                            value={relationship.stage}
                            disabled={move.isPending && move.variables?.id === relationship.id}
                            onChange={(event) =>
                              move.mutate({
                                id: relationship.id,
                                stage: event.currentTarget.value as RelationshipStage,
                              })
                            }
                          >
                            {stages.map((value) => (
                              <option key={value} value={value}>
                                {humanizeStage(value)}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </Surface>
                  ))}
                  {entries.length === 0 ? <p className="pd-column-empty">No relationships here.</p> : null}
                </div>
              </section>
            );
          })}
        </div>
      ) : null}
      <RelationshipDetail
        relationshipId={selectedRelationshipId}
        opened={Boolean(selectedRelationshipId)}
        onClose={() => {
          setSelectedRelationshipId(null);
          const next = new URLSearchParams(searchParams);
          next.delete('focus');
          setSearchParams(next, {replace: true});
        }}
      />

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="Add a recruiter or contact" centered>
        <form
          className="pd-modal-form"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <TextInput
            label="Name"
            required
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            placeholder="Recruiter or contact name"
          />
          <TextInput
            type="email"
            label="Email"
            description="Submitting an address authorizes a matching Gmail thread search when Gmail is connected."
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
            placeholder="recruiter@example.com"
          />
          <div className="pd-permission-note">
            <UsersRound size={18} aria-hidden="true" />
            <p>Internal contact creation is automatic. PursuitDeck never sends email or writes to Google Contacts.</p>
          </div>
          {create.isError ? (
            <p className="pd-form-error" role="alert">
              {create.error instanceof Error ? create.error.message : 'The relationship could not be added.'}
            </p>
          ) : null}
          <div className="pd-form-actions">
            <SecondaryButton type="button" onClick={() => setCreateOpen(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" loading={create.isPending}>
              Add relationship
            </PrimaryButton>
          </div>
        </form>
      </Modal>
      <Modal opened={Boolean(relationshipEdit)} onClose={() => setRelationshipEdit(null)} title="Edit relationship" closeButtonProps={{'aria-label': 'Close relationship editor'}} centered>
        {relationshipEdit ? (
          <form className="pd-modal-form" onSubmit={(event) => { event.preventDefault(); saveRelationship.mutate(relationshipEdit); }}>
            <TextInput label="Name" required value={relationshipEdit.name} onChange={(event) => setRelationshipEdit({...relationshipEdit, name: event.currentTarget.value})} />
            <Textarea label="Emails" description="One email address per line." value={relationshipEdit.emails} onChange={(event) => setRelationshipEdit({...relationshipEdit, emails: event.currentTarget.value})} autosize minRows={2} />
            <TextInput label="Phone" value={relationshipEdit.phone} onChange={(event) => setRelationshipEdit({...relationshipEdit, phone: event.currentTarget.value})} />
            <TextInput label="Agency" value={relationshipEdit.agency} onChange={(event) => setRelationshipEdit({...relationshipEdit, agency: event.currentTarget.value})} />
            <Textarea label="Companies" description="One company per line." value={relationshipEdit.companyNames} onChange={(event) => setRelationshipEdit({...relationshipEdit, companyNames: event.currentTarget.value})} autosize minRows={2} />
            <Textarea label="Next action" value={relationshipEdit.nextAction} onChange={(event) => setRelationshipEdit({...relationshipEdit, nextAction: event.currentTarget.value})} autosize minRows={2} />
            {saveRelationship.isError ? <p className="pd-form-error" role="alert">{saveRelationship.error instanceof Error ? saveRelationship.error.message : 'The relationship could not be saved.'}</p> : null}
            <div className="pd-form-actions"><SecondaryButton type="button" onClick={() => setRelationshipEdit(null)}>Cancel</SecondaryButton><PrimaryButton type="submit" loading={saveRelationship.isPending}>Save relationship</PrimaryButton></div>
          </form>
        ) : null}
      </Modal>
    </>
  );
};
